import type { CameraHashState } from './camera-navigation.ts';
import { formatCameraHash, parseCameraHash } from '../domain/camera-hash.ts';
import type { GeoBounds } from '../domain/coordinates.ts';
import type { CameraState } from '../domain/coordinates.ts';

export function createBrowserCameraHashState(
  browserWindow: Window,
  {
    bounds,
    viewportHeight,
  }: {
    readonly bounds: GeoBounds;
    readonly viewportHeight: () => number;
  },
): CameraHashState {
  const read = (): CameraState | undefined =>
    parseCameraHash(browserWindow.location.hash, {
      bounds,
      viewportHeight: viewportHeight(),
    });

  return Object.freeze({
    read,
    replace(camera: CameraState): void {
      const hash = formatCameraHash(camera, viewportHeight());
      if (hash.localeCompare(browserWindow.location.hash) === 0) return;
      browserWindow.history.replaceState(
        null,
        '',
        `${browserWindow.location.pathname}${browserWindow.location.search}${hash}`,
      );
    },
    subscribe(listener: (camera: CameraState) => void): () => void {
      const handleHashChange = (): void => {
        const camera = read();
        if (camera !== undefined) listener(camera);
      };
      browserWindow.addEventListener('hashchange', handleHashChange);
      return () => {
        browserWindow.removeEventListener('hashchange', handleHashChange);
      };
    },
  });
}
